import { t } from '@lingui/macro';
import { format, parseISO } from 'date-fns';
import isEmpty from 'lodash/isEmpty';

import { activeAppointments } from 'utils/Appointments';

export const COLUMN_NAMES = {
  FAULTS: 'FAULTS',
  RECALLS: 'RECALLS',
  COST_CENTER: 'COST_CENTER',
  SCHEDULED_MAINTENANCES: 'SCHEDULED_MAINTENANCES',
  WARRANTIES: 'WARRANTIES',
  CHASSIS_ID: 'CHASSIS_ID',
  COST_CENTERS: 'COST_CENTERS',
  SERIAL_NUMBER: 'SERIAL_NUMBER',
  UNIT_NUMBER: 'UNIT_NUMBER',
  VIN: 'VIN',
  TYPE: 'TYPE',
  FLEET_INFO: 'FLEET_INFO',
  WORK_IN_PROGRESS: 'WORK_IN_PROGRESS',
  ALERTS: 'ALERTS',
  LOCATION: 'LOCATION',
  ODOMETER: 'ODOMETER',
  ENGINE_HOURS: 'ENGINE_HOURS',
  ENGINE: 'ENGINE',
  CUSTOMER: 'CUSTOMER',
  YEAR: 'YEAR',
  MAKE: 'MAKE',
  MODEL: 'MODEL',
} as const;

let assetTypeLabelsByCode: Record<string, string> = {};

export function setAssetTypeLabels(
  labels: ReadonlyArray<{ code: string; label: string }>,
): void {
  assetTypeLabelsByCode = labels.reduce<Record<string, string>>(
    (acc, { code, label }) => {
      acc[code] = label;
      return acc;
    },
    {},
  );
}

export default function assetTypeText(id: Maybe<string>): string {
  if (id == null || id === '') return t`Unknown`;

  return assetTypeLabelsByCode[id] ?? t`Unknown`;
}

/* istanbul ignore next -- @preserve */
export function assetAttributesForIdentifiers(asset: Asset) {
  const yearMakeModel = [asset?.year, asset?.make, asset?.model]
    .filter(Boolean)
    .join(' ');

  const assetType = assetTypeText(asset.assetType);

  return { ...asset, yearMakeModel, assetType };
}

// receiving the `data` from the `Assets` query, it will extract and transform the data
// so it can be shown on different parts of the application
export function getDataFromAssetQuery(data: { asset: Asset }) {
  if (!data?.asset) {
    return {
      cases: [],
      appointments: [],
      totalAppointmentsAndCases: 0,
      totalAppointmentsServiceRequestsAndCases: 0,
    };
  }

  const platformCaseCount = data?.asset.platformCases.length;

  if (isEmpty(data?.asset.appointments)) {
    return {
      totalAppointmentsServiceRequestsAndCases: platformCaseCount,
      totalAppointmentsAndCases: platformCaseCount,
      appointments: [],
      cases: data?.asset.platformCases,
    };
  }

  const assetActiveAppointments: Appointment[] = activeAppointments(
    data?.asset.appointments,
  );

  const serviceRequestsCount = assetActiveAppointments.reduce(
    (acc: number, appt) => {
      const hasServiceRequest = !!appt.platformServiceRequest;
      return (
        acc +
        (hasServiceRequest ? 1 : /* istanbul ignore next -- @preserve */ 0)
      );
    },
    0,
  );

  const totalAppointmentsAndCases =
    assetActiveAppointments.length + platformCaseCount;

  const totalAppointmentsServiceRequestsAndCases =
    totalAppointmentsAndCases + serviceRequestsCount;

  return {
    totalAppointmentsAndCases,
    totalAppointmentsServiceRequestsAndCases,
    appointments: assetActiveAppointments,
    cases: data?.asset.platformCases,
  };
}

export function formatISODate(
  date: string,
  dateFormat = 'd MMM yyyy, hh:mm aa',
) {
  /* istanbul ignore next -- @preserve */
  if (!date) return undefined;
  return format(parseISO(date), dateFormat);
}
